/* ==========================================================================
   Music toggle

   Rules this file obeys, and which should not be "improved" later:

   1. NO AUTOPLAY. Not muted-then-unmuted, not play() with a caught rejection,
      not on first click anywhere. Every browser blocks audio without a user
      gesture, and a storefront that ambushes a visitor with sound loses them.
      The only thing that starts this audio is a press on the button.

   2. ZERO cost until pressed. The <audio> element carries preload="none", so
      nothing is fetched on page load. The hero image is the LCP element on
      this site and this feature must never compete with it.

   3. State travels, within a visit only. sessionStorage remembers whether the
      song was playing and where it had reached, so moving between pages does
      not restart it from the beginning. A NEW session always starts paused,
      because resuming there would be autoplay by another name.

   The button is hidden in the markup and revealed here. With JavaScript off,
   the visitor never sees a control that cannot work.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__lkMusicInitialised) return;
  window.__lkMusicInitialised = true;

  var STORE_PLAYING = 'lk:music:playing';
  var STORE_TIME = 'lk:music:time';
  var FADE_MS = 600;

  function readStore(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeStore(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) {
      /* Private mode, blocked storage. The control still works, it just
         forgets between pages. Never let this throw. */
    }
  }

  function initMusic(root) {
    if (root.dataset.lkMusicBound === 'true') return;
    root.dataset.lkMusicBound = 'true';

    var audio = root.querySelector('[data-lk-music-audio]');
    var button = root.querySelector('[data-lk-music-toggle]');
    var text = root.querySelector('[data-lk-music-text]');
    if (!audio || !button) return;

    var targetVolume = parseFloat(root.dataset.lkMusicVolume);
    if (isNaN(targetVolume) || targetVolume <= 0 || targetVolume > 1) {
      targetVolume = 0.35;
    }

    var trackLabel = root.dataset.lkMusicLabel || 'music';
    var labelPaused = text ? text.textContent.trim() : '';
    var labelPlaying = button.getAttribute('data-label-playing') || 'Pause the song';

    var fadeTimer = null;
    var playing = false;
    var pausedByTabSwitch = false;

    root.hidden = false;

    /* --- volume fades, so the song never slams in or cuts out ------------- */

    function fadeTo(target, onDone) {
      window.clearInterval(fadeTimer);
      var steps = 24;
      var stepMs = FADE_MS / steps;
      var start = audio.volume;
      var delta = target - start;
      var i = 0;

      fadeTimer = window.setInterval(function () {
        i += 1;
        var next = start + (delta * i) / steps;
        audio.volume = Math.min(1, Math.max(0, next));
        if (i >= steps) {
          window.clearInterval(fadeTimer);
          audio.volume = Math.min(1, Math.max(0, target));
          if (typeof onDone === 'function') onDone();
        }
      }, stepMs);
    }

    function reflectState() {
      root.dataset.lkMusicState = playing ? 'playing' : 'paused';
      button.setAttribute('aria-pressed', playing ? 'true' : 'false');
      button.setAttribute(
        'aria-label',
        (playing ? 'Pause music, ' : 'Play music, ') + trackLabel
      );
      if (text) {
        text.textContent = playing ? labelPlaying : labelPaused;
      }
    }

    function start(fromTime) {
      audio.volume = 0;
      if (typeof fromTime === 'number' && isFinite(fromTime) && fromTime > 0) {
        try {
          audio.currentTime = fromTime;
        } catch (e) {
          /* currentTime can throw before metadata is ready. Starting from the
             top is an acceptable fallback; never let it break playback. */
        }
      }

      var attempt = audio.play();
      if (attempt && typeof attempt.then === 'function') {
        attempt
          .then(function () {
            playing = true;
            reflectState();
            fadeTo(targetVolume);
            writeStore(STORE_PLAYING, '1');
          })
          .catch(function () {
            /* Blocked, or the file could not load. Stay honestly paused
               rather than showing a playing state that is a lie. */
            playing = false;
            reflectState();
            writeStore(STORE_PLAYING, '0');
          });
      } else {
        playing = true;
        reflectState();
        fadeTo(targetVolume);
        writeStore(STORE_PLAYING, '1');
      }
    }

    function stop(remember) {
      fadeTo(0, function () {
        audio.pause();
      });
      playing = false;
      reflectState();
      if (remember !== false) {
        writeStore(STORE_PLAYING, '0');
      }
    }

    button.addEventListener('click', function () {
      if (playing) {
        stop();
      } else {
        start(parseFloat(readStore(STORE_TIME)) || 0);
      }
    });

    /* Keep the resume point fresh, cheaply. */
    audio.addEventListener('timeupdate', function () {
      if (playing) {
        writeStore(STORE_TIME, String(Math.floor(audio.currentTime)));
      }
    });

    /* Leaving the tab should silence the page. Coming back restores only what
       the visitor had chosen. */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        if (playing) {
          pausedByTabSwitch = true;
          stop(false);
        }
      } else if (pausedByTabSwitch) {
        pausedByTabSwitch = false;
        start(parseFloat(readStore(STORE_TIME)) || 0);
      }
    });

    /* Carrying a visit across pages is NOT autoplay: the visitor already
       pressed play in this session, and the browser's own gesture record
       usually carries too. If it does not, the catch above leaves the control
       paused and honest. */
    reflectState();
    if (readStore(STORE_PLAYING) === '1') {
      start(parseFloat(readStore(STORE_TIME)) || 0);
    }
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-lk-music]').forEach(initMusic);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  /* Theme editor: the section can be re-rendered without a page reload. */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
